import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';

import { ajvResolver } from '@hookform/resolvers/ajv';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import {
	autocompleteClasses,
	FormControlLabel,
	ListItem,
	ListItemText,
	Radio,
	RadioGroup,
} from '@mui/material';
import { JSONSchemaType } from 'ajv';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as semver from 'semver';

import { FormContainer, ReactDatePicker, Tooltip } from '~components';
import { useAuthorize } from '~features/authentication';
import { useFormContainerState } from '~hooks';
import { FormWrapperRefProps } from '~interfaces/refProps';

import schema from './skcDeviceCommandSchema.json';
import SkcDevicesAutocomplete from '../../components/autocompletes/skcDevicesAutocomplete';
import SkcDeviceCommandEnum from '../../enums/skcDeviceCommandEnum';
import SkcDeviceCommand from '../../interfaces/skcDeviceCommand';

import './skcDeviceCommandForm.css';

type NullablePartial<T> = { [P in keyof T]: T[P] | null };

const semverRegex = /^elock-(handle|cylinder|wallreader)-*-v(\d+\.\d+\.\d+)$/;

const parseFirmware = (firmware?: string) => {
	if (!firmware) {
		return null;
	}
	const match = firmware.match(semverRegex);
	if (!match) {
		return null;
	}

	return {
		deviceType: match[1] as 'handle' | 'cylinder' | 'wallreader',
		version: match[2],
	};
};

interface AccessCommandFormProps {
	onSubmit?: (value: SkcDeviceCommand) => void;
}

const AccessCommandForm = forwardRef<FormWrapperRefProps, AccessCommandFormProps>(
	({ onSubmit }, ref) => {
		const { t } = useTranslation();
		const { isSuperAdmin } = useAuthorize();

		const { setDisabled } = useFormContainerState();
		const { getValues, control, formState, watch, setValue } = useForm<
			NullablePartial<SkcDeviceCommand>
		>({
			defaultValues: {
				device: null,
				command: null,
				endDate: null,
			},
			mode: 'onChange',
			resolver: ajvResolver(schema as unknown as JSONSchemaType<NullablePartial<SkcDeviceCommand>>),
		});

		const watchDevice = watch('device');
		const watchCommand = watch('command');
		const watchEndDate = watch('endDate');

		const isWallreader = useMemo(() => {
			const parsed = parseFirmware(watchDevice?.firmware);
			return parsed?.deviceType === 'wallreader';
		}, [watchDevice?.firmware]);

		useImperativeHandle(
			ref,
			() => ({
				onSubmit: () => onSubmit?.(getValues() as SkcDeviceCommand),
			}),
			[onSubmit, getValues]
		);

		useEffect(() => {
			if (
				isWallreader &&
				(watchCommand === SkcDeviceCommandEnum.EnableOfficeMode ||
					watchCommand === SkcDeviceCommandEnum.DisableOfficeMode)
			) {
				setValue('command', null, { shouldValidate: true, shouldDirty: true });
				setValue('endDate', null, { shouldValidate: true, shouldDirty: true });
			}
		}, [isWallreader, setValue, watchCommand]);

		useEffect(() => {
			let disabled = !formState.isValid;
			if (watchCommand === SkcDeviceCommandEnum.EnableOfficeMode) {
				if (dayjs(watchEndDate).isBefore(new Date())) {
					disabled = true;
				}
				if (!dayjs(watchEndDate).isValid()) {
					disabled = true;
				}
				if (watchEndDate == null) {
					disabled = true;
				}
			}
			setDisabled?.(disabled);
		}, [formState.isValid, watchCommand, watchEndDate, setDisabled]);

		const authorizedCommandOptions = useMemo(() => {
			const filteredCommandOptions =
				isSuperAdmin() ?
					Object.values(SkcDeviceCommandEnum)
				:	Object.values(SkcDeviceCommandEnum).filter((el) => el !== SkcDeviceCommandEnum.Reboot);

			const deviceFilteredCommandOptions =
				isWallreader ?
					filteredCommandOptions.filter(
						(el) =>
							el !== SkcDeviceCommandEnum.EnableOfficeMode &&
							el !== SkcDeviceCommandEnum.DisableOfficeMode
					)
				:	filteredCommandOptions;

			return deviceFilteredCommandOptions.map((el) => ({
				label: (() => {
					switch (el) {
						case SkcDeviceCommandEnum.Reboot:
							return t('reset');
						case SkcDeviceCommandEnum.EnableOfficeMode:
							return t('enableOfficeMode', { resource: t('officeMode') });
						case SkcDeviceCommandEnum.DisableOfficeMode:
							return t('disableOfficeMode', { resource: t('officeMode') });
						default:
							return t(el);
					}
				})(),
				value: el,
			}));
		}, [isSuperAdmin, isWallreader, t]);

		return (
			<FormContainer>
				<Controller
					name='device'
					control={control}
					render={({ field }) => (
						<SkcDevicesAutocomplete
							value={field.value}
							onChange={(_, newValue, reason) => reason !== 'clear' && field.onChange(newValue)}
							getOptionDisabled={(option) => {
								const parsed = parseFirmware(option.firmware);
								if (parsed == null) {
									return true;
								}

								// This was determined in line with Maurice (hardware). Remote unlock
								// is not supported for versions lower than v0.8.9
								if (parsed.deviceType === 'wallreader') {
									// Wallreaders always support remote unlock
									return semver.lt(parsed.version, '0.9.1');
								}
								return semver.lt(parsed.version, '0.8.9');
							}}
							renderOption={({ key, ...props }, option) => (
								<ListItem
									{...props}
									component='li'
									sx={{
										[`&.${autocompleteClasses.option}`]: {
											py: '2px',
										},
									}}
									secondaryAction={
										props['aria-disabled'] ?
											<Tooltip title={t('cannotSendRemoteUnlockHelperText')} placement='right'>
												<HelpOutlineIcon fontSize='small' />
											</Tooltip>
										:	null
									}
								>
									<ListItemText
										primary={option.label}
										secondary={`${option.id} - ${option.hardwareId}`}
									/>
								</ListItem>
							)}
						/>
					)}
				/>
				{watchDevice != null && (
					<Controller
						name='command'
						control={control}
						render={({ field }) => (
							<RadioGroup {...field} sx={{ width: 'fit-content' }}>
								{authorizedCommandOptions.map((el) => (
									<FormControlLabel
										key={el.value}
										value={el.value}
										control={<Radio />}
										label={el.label}
									/>
								))}
							</RadioGroup>
						)}
					/>
				)}
				{watchCommand === SkcDeviceCommandEnum.EnableOfficeMode && (
					<Controller
						name='endDate'
						control={control}
						render={({ field }) => (
							<ReactDatePicker
								onChange={field.onChange}
								selected={field.value}
								label={t('ui.label.endDate')}
								showTimeSelect
								minTime={new Date()}
								minDate={new Date()}
								maxTime={new Date(2025, 1, 1, 23, 59)}
							/>
						)}
					/>
				)}
			</FormContainer>
		);
	}
);

export default AccessCommandForm;
